import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Badge,
  Spinner,
  Alert,
  Nav,
  Dropdown,
} from 'react-bootstrap';
import SuperAdminSidebar from '../components/SuperAdminSidebar';
import AdminTopBar from '../components/AdminTopBar';
import { API_ENDPOINTS } from '../config/api';
import { useInstitutionalData, useInstitutionalResources } from '../contexts/InstitutionalDataContext';

const RECURRENCE_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'two_months', label: 'Every two months' },
  { value: 'three_months', label: 'Quarterly' },
  { value: 'four_months', label: 'Every four months' },
  { value: 'six_months', label: 'Biannual' },
  { value: 'yearly', label: 'Annual' },
];

const InstitutionalEvaluationsPage = () => {
  const navigate = useNavigate();
  // Programs + users come from the shared institutional cache so navigating
  // here from the dashboard or user/program management page is instant.
  const { entries, loading: resourcesLoading, error: resourceError } = useInstitutionalResources([
    'evaluationTemplates',
    'evaluationResults',
    'programs',
    'users',
  ]);
  const { invalidate } = useInstitutionalData();
  const templates = entries.evaluationTemplates?.data || [];
  const resultsSummaryCached = entries.evaluationResults?.data || [];
  const programs = entries.programs?.data || [];
  const users = entries.users?.data || [];
  // Render as soon as templates are available; the completion-rate summary
  // may take longer because of its aggregation queries.
  const loading =
    !entries.evaluationTemplates?.data &&
    (entries.evaluationTemplates?.loading || resourcesLoading);
  const [error, setError] = useState('');

  const [showDefaultsModal, setShowDefaultsModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null);

  const [defaultProgramSelection, setDefaultProgramSelection] = useState({});
  const [defaultsAllPrograms, setDefaultsAllPrograms] = useState(true);

  const [deployAudience, setDeployAudience] = useState('residents');
  const [deployEvalType, setDeployEvalType] = useState('survey');
  const [deployAllPrograms, setDeployAllPrograms] = useState(true);
  const [deployProgramSelection, setDeployProgramSelection] = useState({});
  const [deployStartDate, setDeployStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [deployStartTime, setDeployStartTime] = useState('08:00');
  const [deployEndDate, setDeployEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [deployEndTime, setDeployEndTime] = useState('17:00');
  const [deployRecurrence, setDeployRecurrence] = useState('monthly');
  const [deployEvaluatorId, setDeployEvaluatorId] = useState('');
  const [deployEvaluatorType, setDeployEvaluatorType] = useState('director');
  const [deploySubmitting, setDeploySubmitting] = useState(false);
  const resultsSummary = resultsSummaryCached;
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [activeResults, setActiveResults] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseLoading, setResponseLoading] = useState(false);
  const [activeResponse, setActiveResponse] = useState(null);
  const [resultsSearch, setResultsSearch] = useState('');
  const [resultsRoleFilter, setResultsRoleFilter] = useState('all');
  const [resultsProgramFilter, setResultsProgramFilter] = useState('all');
  const [resultsStatusFilter, setResultsStatusFilter] = useState('all');
  const [removingAssignmentId, setRemovingAssignmentId] = useState(null);
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('institutionalEvaluationsTab') || 'templates');

  const breadcrumbItems = useMemo(
    () => [
      { href: '/institutional-admin/dashboard', label: 'Dashboard' },
      { label: 'Evaluations' },
    ],
    []
  );

  const loadData = useCallback(() => {
    invalidate(['evaluationTemplates', 'evaluationResults', 'programs', 'users']);
  }, [invalidate]);

  useEffect(() => {
    const role = localStorage.getItem('role');
    const t = localStorage.getItem('token');
    if (!t || role !== 'institutional_admin') {
      window.location.replace('/');
    }
  }, []);

  useEffect(() => {
    if (resourceError) setError(resourceError);
  }, [resourceError]);

  const openDefaultsModal = (tpl) => {
    setActiveTemplate(tpl);
    setDefaultsAllPrograms(true);
    const sel = {};
    programs.forEach((p) => {
      sel[p.id] = true;
    });
    setDefaultProgramSelection(sel);
    setShowDefaultsModal(true);
  };

  const submitDefaults = async () => {
    const t = localStorage.getItem('token');
    if (!activeTemplate || !t) return;
    const programIds = defaultsAllPrograms
      ? 'all'
      : Object.entries(defaultProgramSelection)
          .filter(([, v]) => v)
          .map(([id]) => parseInt(id, 10));
    if (!defaultsAllPrograms && programIds.length === 0) {
      setError('Select at least one program');
      return;
    }
    try {
      const res = await fetch(
        `${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/evaluation-templates/${activeTemplate.id}/set-defaults`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${t}`,
          },
          body: JSON.stringify({ programIds, clearOthers: true }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to update defaults');
      }
      setShowDefaultsModal(false);
      loadData();
    } catch (e) {
      setError(e.message);
    }
  };

  const openDeployModal = (tpl) => {
    setActiveTemplate(tpl);
    setDeployAudience('residents');
    setDeployEvalType('survey');
    setDeployAllPrograms(true);
    const sel = {};
    programs.forEach((p) => {
      sel[p.id] = true;
    });
    setDeployProgramSelection(sel);
    setDeployEvaluatorId('');
    setDeployEvaluatorType('director');
    setShowDeployModal(true);
  };

  const evaluatorChoices = useMemo(() => {
    return users.filter(
      (u) =>
        (u.role === 'director' || u.role === 'attending') &&
        programs.some((p) => p.id === u.programId)
    );
  }, [users, programs]);

  const submitDeploy = async () => {
    const t = localStorage.getItem('token');
    if (!activeTemplate || !t) return;
    const programIds = deployAllPrograms
      ? 'all'
      : Object.entries(deployProgramSelection)
          .filter(([, v]) => v)
          .map(([id]) => parseInt(id, 10));
    if (!deployAllPrograms && programIds.length === 0) {
      setError('Select at least one program');
      return;
    }
    if ((deployEvalType === 'performance' || deployEvalType === 'feedback') && !deployEvaluatorId) {
      setError('Select an evaluator for performance or feedback deployments');
      return;
    }
    setDeploySubmitting(true);
    setError('');
    try {
      const body = {
        audience: deployAudience,
        programIds,
        evaluationType: deployEvalType,
        startDate: deployStartDate,
        startTime: deployStartTime,
        endDate: deployEndDate,
        endTime: deployEndTime,
        isTimed: false,
        isAnonymous: false,
        recurrence: deployEvalType === 'performance' ? deployRecurrence : null,
        evaluatorId:
          deployEvalType === 'performance' || deployEvalType === 'feedback'
            ? parseInt(deployEvaluatorId, 10)
            : undefined,
        evaluatorType:
          deployEvalType === 'performance' || deployEvalType === 'feedback'
            ? deployEvaluatorType
            : undefined,
      };
      const res = await fetch(
        `${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/evaluation-templates/${activeTemplate.id}/deploy`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${t}`,
          },
          body: JSON.stringify(body),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || 'Deploy failed');
      }
      setShowDeployModal(false);
      loadData();
      alert(data.message || `Created ${data.evaluationsCreated || 0} evaluation instance(s).`);
    } catch (e) {
      setError(e.message);
    } finally {
      setDeploySubmitting(false);
    }
  };

  const openResultsModal = async (template) => {
    const t = localStorage.getItem('token');
    if (!t) return;
    setResultsLoading(true);
    setActiveResults(null);
    setShowResultsModal(true);
    try {
      const res = await fetch(
        `${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/evaluation-templates/${template.id}/results`,
        { headers: { Authorization: `Bearer ${t}` } }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to load result details');
      setActiveResults(data);
    } catch (e) {
      setError(e.message);
      setShowResultsModal(false);
    } finally {
      setResultsLoading(false);
    }
  };

  const removeAssignment = async (assignmentId) => {
    const t = localStorage.getItem('token');
    if (!t || !assignmentId) return;
    if (!window.confirm('Remove this pending assignment?')) return;
    setRemovingAssignmentId(assignmentId);
    try {
      const res = await fetch(`${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/evaluation-assignments/${assignmentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${t}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to remove assignment');

      if (activeResults?.template?.id) {
        await openResultsModal({ id: activeResults.template.id });
      } else {
        await loadData();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setRemovingAssignmentId(null);
    }
  };

  const openResponseModal = async (responseId) => {
    const t = localStorage.getItem('token');
    if (!t || !responseId) return;
    setResponseLoading(true);
    setShowResponseModal(true);
    setActiveResponse(null);
    try {
      const res = await fetch(`${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/evaluation-responses/${responseId}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to load response');
      setActiveResponse(data);
    } catch (e) {
      setError(e.message);
      setShowResponseModal(false);
    } finally {
      setResponseLoading(false);
    }
  };

  const openSurveyResultsPage = (templateId) => {
    if (!templateId) return;
    localStorage.setItem('institutionalEvaluationsTab', 'completion');
    navigate(`/institutional-admin/evaluations/survey-results/${templateId}`);
  };

  const openCompletedEvalDetailsPage = (templateId) => {
    if (!templateId) return;
    localStorage.setItem('institutionalEvaluationsTab', 'completion');
    navigate(`/institutional-admin/evaluations/completed/${templateId}`);
  };

  if (loading) {
    return (
      <div className="d-flex vh-100 overflow-hidden bg-light">
        <SuperAdminSidebar variant="institutional" />
        <div className="d-flex flex-column flex-grow-1 min-w-0 overflow-auto justify-content-center align-items-center">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  const filteredAssignments = (activeResults?.assignments || []).filter((a) => {
    const search = resultsSearch.trim().toLowerCase();
    const assigneeName = a.assignee ? `${a.assignee.firstName} ${a.assignee.lastName}` : '';
    const assignedToName = a.assignedTo ? `${a.assignedTo.firstName} ${a.assignedTo.lastName}` : '';
    const role = (a.assignee?.role || '').toLowerCase();
    const status = (a.status || '').toLowerCase();
    const programId = String(a.assignee?.programId || '');

    const matchesSearch =
      !search ||
      assigneeName.toLowerCase().includes(search) ||
      assignedToName.toLowerCase().includes(search) ||
      (a.assignee?.email || '').toLowerCase().includes(search);
    const matchesRole = resultsRoleFilter === 'all' || role === resultsRoleFilter;
    const matchesProgram = resultsProgramFilter === 'all' || programId === resultsProgramFilter;
    const matchesStatus = resultsStatusFilter === 'all' || status === resultsStatusFilter;
    return matchesSearch && matchesRole && matchesProgram && matchesStatus;
  });

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('institutionalEvaluationsTab', tab);
  };

  return (
    <div className="d-flex vh-100 overflow-hidden">
      <SuperAdminSidebar variant="institutional" />
      <div className="d-flex flex-column flex-grow-1 min-w-0 overflow-auto">
        <AdminTopBar variant="institutional" breadcrumbItems={breadcrumbItems} />
        <Container fluid className="bg-inverse-light">
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Row className="g-3">
            <Col md={3} className="border-end bg-inverse-light pt-3 adjust-min-vh">
              <div className="d-flex align-items-center justify-content-between mb-3 px-2">
                <div>
                  <h6 className="mb-0">Evaluations</h6>
                  <small className="text-muted">Institution tools</small>
                </div>
                <Button variant="primary" size="sm" onClick={() => navigate('/institutional-admin/evaluations/new')}>
                  <i className="bi bi-plus-lg"></i>
                </Button>
              </div>
              <Nav variant="secondary-tabs" className="flex-column" activeKey={activeTab} onSelect={(k) => handleTabChange(k || 'templates')}>
                <Nav.Item>
                  <Nav.Link eventKey="templates" className="d-flex align-items-center justify-content-between">
                    Templates
                    <Badge bg="secondary" className="ms-2">{templates.length}</Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="completion" className="d-flex align-items-center justify-content-between">
                    Completion Evals
                    <Badge bg="secondary" className="ms-2">{resultsSummary.length}</Badge>
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>

            <Col md={9} className="adjust-min-vh p-0">
              {activeTab === 'templates' && (
                <>
                  <Card className="p-0 border-0 rounded-0 h-100">
                    <Card.Header className="d-flex justify-content-between align-items-center gap-3 px-4 py-3 custom-sticky-top border-bottom-0">
                      <div className="d-flex flex-column">
                        <h6 className="mb-0">Evaluation Templates</h6>
                        <div className="text-muted small">
                          Create reusable forms, set program defaults, and deploy to cohorts.
                        </div>
                      </div>
                      <div className="ms-auto">
                        <Button variant="primary" size="sm" onClick={() => navigate('/institutional-admin/evaluations/new')}>
                          New Template
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <div className="table-responsive alt-table-striped programs-table-scroll-wrapper">
                      <Table hover className="table-header-sticky mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>Title</th>
                            <th>Type</th>
                            <th>Default programs</th>
                            <th>Last deployed</th>
                            <th className="text-end">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {templates.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="text-center text-muted py-4">
                                No institution templates yet.
                              </td>
                            </tr>
                          ) : (
                            templates.map((t) => (
                              <tr key={t.id}>
                                <td className="fw-semibold">{t.title}</td>
                                <td><Badge bg="secondary">{t.evaluationType}</Badge></td>
                                <td>{t.defaultProgramCount ?? 0}</td>
                                <td>{t.lastDeployedAt ? new Date(t.lastDeployedAt).toLocaleString() : '—'}</td>
                                <td className="text-end text-nowrap">
                                  <Dropdown>
                                    <Dropdown.Toggle variant="light-lite" size="sm" className="no-arrow">
                                      <i className="bi bi-three-dots-vertical"></i>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                      <Dropdown.Item onClick={() => navigate(`/institutional-admin/evaluations/edit/${t.id}`)}>Edit</Dropdown.Item>
                                      <Dropdown.Item onClick={() => openDefaultsModal(t)}>Defaults</Dropdown.Item>
                                      <Dropdown.Item onClick={() => openDeployModal(t)}>Deploy</Dropdown.Item>
                                      <Dropdown.Item onClick={() => openResultsModal(t)}>Results</Dropdown.Item>
                                    </Dropdown.Menu>
                                  </Dropdown>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </>
              )}

              {activeTab === 'completion' && (
                <>
                  <Card className="p-0 border-0 rounded-0 h-100">
                    <Card.Header className="d-flex px-4 py-3 justify-content-between align-items-center custom-sticky-top border-bottom-0">
                      <div className="d-flex flex-column">
                        <h6 className="mb-0">Completion Evaluations</h6>
                        <div className="text-muted small">
                          Track completion rates and drill into submitted responses.
                        </div>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <div className="table-responsive alt-table-striped">
                      <Table hover className="table-header-sticky mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>Template</th>
                            <th>Type</th>
                            <th>Assigned</th>
                            <th>Completed</th>
                            <th>Pending</th>
                            <th>Rate</th>
                            <th className="text-end">Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resultsSummary.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="text-center text-muted py-4">
                                No deployed evaluations yet.
                              </td>
                            </tr>
                          ) : (
                            resultsSummary.map((r) => (
                              <tr key={r.templateId}>
                                <td className="fw-semibold">{r.title}</td>
                                <td><Badge bg="secondary">{r.evaluationType}</Badge></td>
                                <td>{r.assignedCount}</td>
                                <td>{r.completedCount}</td>
                                <td>{r.pendingCount}</td>
                                <td>{r.completionRate}%</td>
                                <td className="text-end">
                                  <div className="d-flex justify-content-end gap-1">
                                    <Button
                                      size="sm"
                                      variant="outline-success"
                                      onClick={() => openCompletedEvalDetailsPage(r.templateId)}
                                    >
                                      View
                                    </Button>
                                    {(r.evaluationType === 'survey' || r.evaluationType === 'questionnaire') && (
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => openSurveyResultsPage(r.templateId)}
                                      >
                                        Summary
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </>
              )}
            </Col>
          </Row>
        </Container>

        <Modal show={showDefaultsModal} onHide={() => setShowDefaultsModal(false)} centered size="md">
          <Modal.Header closeButton>
            <Modal.Title>Director template defaults</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted small">
              Selected programs will see this template in the director evaluation builder &ldquo;Load from
              Template&rdquo; list.
            </p>
            <Form.Check
              type="checkbox"
              id="defaults-all-prog"
              label="All programs in this institution"
              checked={defaultsAllPrograms}
              onChange={(e) => setDefaultsAllPrograms(e.target.checked)}
              className="mb-3"
            />
            {!defaultsAllPrograms && (
              <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
                {programs.map((p) => (
                  <Form.Check
                    key={p.id}
                    type="checkbox"
                    id={`def-prog-${p.id}`}
                    label={p.name}
                    checked={!!defaultProgramSelection[p.id]}
                    onChange={(e) =>
                      setDefaultProgramSelection((prev) => ({
                        ...prev,
                        [p.id]: e.target.checked,
                      }))
                    }
                  />
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDefaultsModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submitDefaults}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showDeployModal} onHide={() => !deploySubmitting && setShowDeployModal(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Deploy evaluations</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted small">
              Creates pending evaluations and assignments for users in the selected programs. Performance and
              feedback deployments target residents only and require one evaluator for all assignments.
            </p>
            <Row className="g-2 mb-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Audience</Form.Label>
                  <Form.Select
                    value={deployAudience}
                    onChange={(e) => setDeployAudience(e.target.value)}
                  >
                    <option value="residents">Residents</option>
                    <option value="attendings">Attendings</option>
                    <option value="directors">Directors</option>
                    <option value="all">All roles (residents, attendings, directors)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Evaluation type for this deployment</Form.Label>
                  <Form.Select
                    value={deployEvalType}
                    onChange={(e) => setDeployEvalType(e.target.value)}
                  >
                    <option value="survey">Survey</option>
                    <option value="questionnaire">Questionnaire</option>
                    <option value="performance">Performance</option>
                    <option value="feedback">Feedback</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Check
              type="checkbox"
              id="deploy-all-prog"
              label="All programs"
              checked={deployAllPrograms}
              onChange={(e) => setDeployAllPrograms(e.target.checked)}
              className="mb-2"
            />
            {!deployAllPrograms && (
              <div className="border rounded p-2 mb-3" style={{ maxHeight: 160, overflowY: 'auto' }}>
                {programs.map((p) => (
                  <Form.Check
                    key={p.id}
                    type="checkbox"
                    id={`dep-prog-${p.id}`}
                    label={p.name}
                    checked={!!deployProgramSelection[p.id]}
                    onChange={(e) =>
                      setDeployProgramSelection((prev) => ({
                        ...prev,
                        [p.id]: e.target.checked,
                      }))
                    }
                  />
                ))}
              </div>
            )}
            <Row className="g-2 mb-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Start</Form.Label>
                  <Row className="g-1">
                    <Col>
                      <Form.Control
                        type="date"
                        value={deployStartDate}
                        onChange={(e) => setDeployStartDate(e.target.value)}
                      />
                    </Col>
                    <Col>
                      <Form.Control
                        type="time"
                        value={deployStartTime}
                        onChange={(e) => setDeployStartTime(e.target.value)}
                      />
                    </Col>
                  </Row>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>End</Form.Label>
                  <Row className="g-1">
                    <Col>
                      <Form.Control
                        type="date"
                        value={deployEndDate}
                        onChange={(e) => setDeployEndDate(e.target.value)}
                      />
                    </Col>
                    <Col>
                      <Form.Control
                        type="time"
                        value={deployEndTime}
                        onChange={(e) => setDeployEndTime(e.target.value)}
                      />
                    </Col>
                  </Row>
                </Form.Group>
              </Col>
            </Row>
            {deployEvalType === 'performance' && (
              <Form.Group className="mb-2">
                <Form.Label>Recurrence</Form.Label>
                <Form.Select
                  value={deployRecurrence}
                  onChange={(e) => setDeployRecurrence(e.target.value)}
                >
                  {RECURRENCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            {(deployEvalType === 'performance' || deployEvalType === 'feedback') && (
              <Row className="g-2">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Evaluator role</Form.Label>
                    <Form.Select
                      value={deployEvaluatorType}
                      onChange={(e) => setDeployEvaluatorType(e.target.value)}
                    >
                      <option value="director">Program director</option>
                      <option value="attending">Attending</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Evaluator</Form.Label>
                    <Form.Select
                      value={deployEvaluatorId}
                      onChange={(e) => setDeployEvaluatorId(e.target.value)}
                    >
                      <option value="">Select…</option>
                      {evaluatorChoices
                        .filter((u) =>
                          deployEvaluatorType === 'director'
                            ? u.role === 'director'
                            : u.role === 'attending'
                        )
                        .map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.firstName} {u.lastName} ({u.role})
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" disabled={deploySubmitting} onClick={() => setShowDeployModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={deploySubmitting} onClick={submitDeploy}>
              {deploySubmitting ? 'Deploying…' : 'Deploy'}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showResultsModal} onHide={() => setShowResultsModal(false)} centered size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              {activeResults?.template?.title || 'Template Results'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {resultsLoading ? (
              <div className="text-center py-4"><Spinner animation="border" /></div>
            ) : !activeResults ? (
              <Alert variant="warning" className="mb-0">No result data found.</Alert>
            ) : (
              <>
                <div className="mb-3 d-flex gap-3">
                  <Badge bg="info">Assigned: {activeResults.summary?.assignedCount ?? 0}</Badge>
                  <Badge bg="success">Completed: {activeResults.summary?.completedCount ?? 0}</Badge>
                  <Badge bg="warning" text="dark">Pending: {activeResults.summary?.pendingCount ?? 0}</Badge>
                </div>
                <Row className="g-2 mb-3">
                  <Col md={4}>
                    <Form.Control
                      placeholder="Search by user, email, assigned-to..."
                      value={resultsSearch}
                      onChange={(e) => setResultsSearch(e.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Select value={resultsRoleFilter} onChange={(e) => setResultsRoleFilter(e.target.value)}>
                      <option value="all">All roles</option>
                      <option value="resident">Resident</option>
                      <option value="attending">Attending</option>
                      <option value="director">Director</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Select
                      value={resultsProgramFilter}
                      onChange={(e) => setResultsProgramFilter(e.target.value)}
                    >
                      <option value="all">All programs</option>
                      {programs.map((p) => (
                        <option key={p.id} value={String(p.id)}>
                          {p.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Select
                      value={resultsStatusFilter}
                      onChange={(e) => setResultsStatusFilter(e.target.value)}
                    >
                      <option value="all">All statuses</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                    </Form.Select>
                  </Col>
                </Row>
                <Table responsive hover size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Assigned To</th>
                      <th>Status</th>
                      <th>Completed At</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssignments.length ? filteredAssignments.map((a) => (
                      <tr key={a.assignmentId}>
                        <td>{a.assignee ? `${a.assignee.firstName} ${a.assignee.lastName}` : 'Unknown'}</td>
                        <td>{a.assignee?.role || '—'}</td>
                        <td>{a.assignedTo ? `${a.assignedTo.firstName} ${a.assignedTo.lastName}` : 'Self'}</td>
                        <td>
                          <Badge bg={a.status === 'completed' ? 'success' : 'secondary'}>
                            {a.status}
                          </Badge>
                        </td>
                        <td>{a.completedAt ? new Date(a.completedAt).toLocaleString() : '—'}</td>
                        <td className="text-end">
                          {a.evaluation?.responseId ? (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => openResponseModal(a.evaluation.responseId)}
                            >
                              View Result
                            </Button>
                          ) : (
                            <span className="text-muted me-2">Not submitted</span>
                          )}
                          {a.status !== 'completed' && !a.evaluation?.responseId && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              disabled={removingAssignmentId === a.assignmentId}
                              onClick={() => removeAssignment(a.assignmentId)}
                            >
                              {removingAssignmentId === a.assignmentId ? 'Removing...' : 'Remove'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-3">
                          No assignments match current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </>
            )}
          </Modal.Body>
        </Modal>

        <Modal show={showResponseModal} onHide={() => setShowResponseModal(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Evaluation Result</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {responseLoading ? (
              <div className="text-center py-4"><Spinner animation="border" /></div>
            ) : !activeResponse ? (
              <Alert variant="warning" className="mb-0">No response loaded.</Alert>
            ) : (
              <>
                <h6 className="mb-2">{activeResponse.template?.title}</h6>
                <div className="text-muted small mb-3">
                  Submitted: {activeResponse.createdAt ? new Date(activeResponse.createdAt).toLocaleString() : '—'}
                </div>
                <Table responsive size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: '55%' }}>Question</th>
                      <th>Answer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(activeResponse.template?.questions || []).map((q) => {
                      const raw = activeResponse.answers?.[q.id];
                      const answerText = Array.isArray(raw)
                        ? raw.join(', ')
                        : typeof raw === 'object' && raw !== null
                          ? JSON.stringify(raw)
                          : (raw ?? '—');
                      return (
                        <tr key={q.id}>
                          <td>{q.questionText}</td>
                          <td>{String(answerText)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default InstitutionalEvaluationsPage;
